import React from "react";
import BWModels from "@benjamin-west-rpm2/benjamin-west-models";
import AddCircle from "@material-ui/icons/AddCircle";
import Edit from "@material-ui/icons/Edit";
import Cancel from "@material-ui/icons/Cancel";
import Chip from "@material-ui/core/Chip";
import PropTypes from "prop-types";
import styled from "styled-components";
import _get from "lodash/get";

import { BWGridAPI, GridHeader, GridTable } from "../../../../ui/BWGrid";
import SpecCategory from "../../../../../models/SpecCategory";
import Paper from "../../../../mui/core/Paper";
import { getKeyColumn } from "./KeyColumn";
import TagText from "../../../../ui/Tags/TagText";
import propTypes from "../../../../../constants/propTypes";
import useUserRole from "../../../../hooks/useUserRole";
import { isSuperAdmin } from "../../../../../utils/roleUtils";

export const StyledChip = styled(({ invert, ...props }) => <Chip {...props} />)`
  background: ${({ invert }) => (invert ? "#F1F1F1" : "#57ABFF")};
  color: ${({ invert }) => (invert ? "#57ABFF" : "#FFFFFF")};
`;

const TypeColumn = ({ type }) =>
  type && <StyledChip invert={type === "FF&E"} label={type} />;

const baseColumns = [
  "name",
  "type",
  "templateCategory.name",
  "key",
  "isActive",
];

export const getColumns = userRole => {
  const columns = [...baseColumns];
  if (isSuperAdmin(userRole)) {
    columns.push("scope.id");
  }
  return columns;
};

export const columnOptions = (templateCategories, scopes) => {
  const specCategorySchema = BWModels.loadSchema("SpecCategory");
  const types = specCategorySchema.__utils.types;
  return {
    name: { bold: true, filter: true },
    type: {
      fill: true,
      render: TypeColumn,
      filter: "select",
      filterOptions: {
        options: types,
      },
    },
    "templateCategory.name": {
      title: "Category",
      filter: "select",
      filterOptions: {
        operator: ({ value }) => ({
          "templateCategory.id": value,
        }),
        options: templateCategories,
      },
    },
    key: {
      title: "Template Type",
      render: getKeyColumn,
      filter: "select",
      filterOptions: {
        options: [
          { id: "COM_ITEM", name: "COM" },
          { id: "FREIGHT_ITEM", name: "Freight" },
          { id: "INSTALL_ITEM", name: "Install" },
          { id: "OTHER_COST", name: "Other Costs" },
          { id: "VENDOR_FREIGHT_ITEM", name: "Vendor Freight" },
          { id: "WAREHOUSING_ITEM", name: "Warehousing" },
        ],
      },
    },
    isActive: {
      title: "Status",
      filter: "select",
      filterOptions: {
        initialOption: true,
        options: [
          { id: true, name: "Active" },
          { id: false, name: "Inactive" },
        ],
      },
      render: row => {
        const isActive = _get(row, "isActive", false);
        const text = isActive ? "Active" : "Inactive";
        return (
          row.id && (
            <TagText intent={isActive ? "success" : undefined}>{text}</TagText>
          )
        );
      },
    },
    "scope.id": {
      title: "Scope",
      filter: "select",
      render: ({ scope }) => scope?.name,
      filterOptions: {
        options: scopes,
      },
    },
  };
};

const getRowMenu = (onOpenEditModal, onOpenDeleteModal) => {
  return [
    {
      icon: <Edit />,
      text: "Edit",
      onClick: onOpenEditModal,
    },
    { icon: <Cancel />, text: "Delete", onClick: onOpenDeleteModal },
  ];
};

const TemplatesPage = ({
  dataComponent,
  templateCategories,
  scopes,
  onOpenCreateModal,
  onOpenEditModal,
  onOpenDeleteModal,
}) => {
  const userRole = useUserRole();

  return (
    <Paper>
      <BWGridAPI
        dataComponent={dataComponent}
        model={SpecCategory}
        includes={["specCategoryDetails", "templateCategory", "scope"]}
        apiRoute="spec-categories"
        defaultSorting={[{ columnName: "name", direction: "asc" }]}
        rootFilters={{ displayInactive: true }}
      >
        <GridHeader
          headerText={`${dataComponent.totalRows} Spec Details Templates`}
          actions={[
            {
              text: "CREATE TEMPLATE",
              icon: <AddCircle />,
              handler: onOpenCreateModal,
            },
          ]}
        />
        <GridTable
          columns={getColumns(userRole)}
          columnOptions={columnOptions(templateCategories, scopes)}
          rowMenu={getRowMenu(onOpenEditModal, onOpenDeleteModal)}
        />
      </BWGridAPI>
    </Paper>
  );
};
TemplatesPage.propTypes = {
  templateCategories: PropTypes.arrayOf(propTypes.templateCategory),
  scopes: PropTypes.array,
  dataComponent: PropTypes.object.isRequired,
  onOpenCreateModal: PropTypes.func.isRequired,
  onOpenEditModal: PropTypes.func.isRequired,
  onOpenDeleteModal: PropTypes.func.isRequired,
};
export default TemplatesPage;
